import AkyoQuestion, {type NativeQuestion} from './AkyoQuestion';
import { useEffect, useState } from "react";
import { Check, KeyRound, LogOut, Radio, RefreshCw } from "lucide-react";

type JoinState = {
  native?: NativeQuestion;
  session: {
    id: string;
    code: string;
    mode: "prerequisite" | "evaluated";
    theme: string;
    chapter: string;
    status: "lobby" | "question" | "reveal" | "finished";
    currentIndex: number;
    totalQuestions: number;
  };
  student: { fullName: string; studentCode: string };
  buttonOrder: number[];
  selectedAnswer: number | null;
};

async function request<T>(path: string, options?: RequestInit) {
  const response = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(options?.headers ?? {}) },
    ...options,
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.error ?? "Une erreur est survenue") as Error & { status?: number; loginRequired?: boolean };
    error.status = response.status;
    error.loginRequired = Boolean(body.loginRequired);
    throw error;
  }
  return body as T;
}

export default function QuizStudentApp() {
  const code = window.location.pathname.match(/^\/qcm\/([0-9]{6})/)?.[1] ?? "";
  const [state, setState] = useState<JoinState | null>(null);
  const [needLogin, setNeedLogin] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  async function join(silent = false) {
    if (!code) { setError("Code QCM invalide"); return; }
    try {
      const result = await request<JoinState>(`/api/qcm/join/${code}`);
      setState(result);
      setNeedLogin(false);
      if (!silent) setError("");
    } catch (reason) {
      const failure = reason as Error & { status?: number; loginRequired?: boolean };
      if (failure.status === 401 || failure.loginRequired) setNeedLogin(true);
      else if (!silent) setError(failure.message);
    }
  }

  useEffect(() => { void join(); }, [code]);
  useEffect(() => {
    if (!state || state.session.status === "finished") return;
    const timer = window.setInterval(() => void join(true), 750);
    return () => window.clearInterval(timer);
  }, [state?.session.id, state?.session.status]);

  async function login(event: React.FormEvent) {
    event.preventDefault();
    setError(""); setSending(true);
    try {
      const result = await request<{ user: { role: string } }>("/api/auth/login", { method: "POST", body: JSON.stringify({ identifier, password }) });
      if (result.user.role !== "student") throw new Error("Utilise ton compte élève pour rejoindre le QCM");
      await join();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Connexion impossible"); }
    finally { setSending(false); }
  }

  async function answer(answerIndex: number) {
    if (!state || state.session.status !== "question" || sending) return;
    setSending(true); setError("");
    setState({ ...state, selectedAnswer: answerIndex });
    try {
      await request(`/api/qcm/join/${code}/answer`, { method: "POST", body: JSON.stringify({ answerIndex }) });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Réponse non enregistrée");
      await join(true);
    } finally { setSending(false); }
  }

  async function logout() {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    setState(null); setNeedLogin(true); setIdentifier(""); setPassword(""); setError("");
  }

  if (needLogin && !state) return <main className="qcm-student-shell qcm-login-screen">
    <section className="qcm-student-card">
      <div className="qcm-student-logo"><img src="/competenceo-logo-192.png" alt="CompetenceO" /><span>QCM</span></div>
      <div className="qcm-code-pill">Code {code}</div>
      <KeyRound size={34} />
      <h1>Connecte-toi</h1>
      <p>Utilise le code élève et le mot de passe de ton compte CompetenceO.</p>
      <form onSubmit={login}>
        <label><span>Code élève</span><input autoCapitalize="characters" autoComplete="username" value={identifier} onChange={(event) => setIdentifier(event.target.value)} placeholder="Ex. DUP-4821" required /></label>
        <label><span>Mot de passe</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
        {error && <div className="qcm-student-error">{error}</div>}
        <button className="qcm-student-primary" disabled={sending}>{sending ? "Connexion…" : "Rejoindre le QCM"}</button>
      </form>
    </section>
  </main>;

  if (!state) return <main className="qcm-student-shell"><section className="qcm-student-card qcm-loading"><RefreshCw className="spin" size={32} /><h1>Connexion au QCM…</h1>{error && <><p>{error}</p><button className="qcm-student-primary" onClick={() => void join()}>Réessayer</button></>}</section></main>;

  const { session, student, buttonOrder, selectedAnswer } = state;
  const letters = ["A", "B", "C", "D"];

  return <main className="qcm-student-shell">
    <header className="qcm-student-head"><div><img src="/competenceo-logo-192.png" alt="" /><span>{student.fullName}</span></div><button onClick={() => void logout()} aria-label="Se déconnecter"><LogOut size={18} /></button></header>

    {session.status === "lobby" && <section className="qcm-student-state"><Radio size={44} /><span>{session.theme ? `${session.theme} · ` : ""}{session.chapter}</span><h1>Tu es connecté</h1><p>Regarde le tableau. Le professeur va lancer la première question.</p><div className="qcm-waiting-dots"><i /><i /><i /></div></section>}

    {session.status === "question" && <section className="qcm-student-question">
      <div className="qcm-mobile-progress"><span>Question {session.currentIndex + 1}</span><small>{session.currentIndex + 1} / {session.totalQuestions}</small></div>
      <div className="qcm-look-board"><strong>Lis la question au tableau</strong><span>Puis choisis ta réponse ici.</span></div>
      {state.native && <AkyoQuestion key={state.native.instanceId} question={state.native} disabled={sending} onAnswer={async answer=>{await request('/api/qcm/join/'+code+'/answer',{method:'POST',body:JSON.stringify({answer})});}}/>}
      {!state.native && <div className="qcm-answer-grid">{buttonOrder.map((answerIndex) => <button key={answerIndex} className={selectedAnswer === answerIndex ? "selected" : ""} disabled={sending} onClick={() => void answer(answerIndex)}><span>{letters[answerIndex]}</span>{selectedAnswer === answerIndex && <Check size={24} />}</button>)}</div>}
      {!state.native && <p className="qcm-answer-help">{selectedAnswer === null ? "Tu peux répondre dès que tu as choisi." : `Réponse ${letters[selectedAnswer]} enregistrée. Tu peux la modifier tant que le professeur n’a pas fermé la question.`}</p>}
      {error && <div className="qcm-student-error">{error}</div>}
    </section>}

    {session.status === "reveal" && <section className="qcm-student-state"><Check size={44} /><span>Question {session.currentIndex + 1} / {session.totalQuestions}</span><h1>Réponses fermées</h1><p>Regarde la correction au tableau. Ton téléphone ne t’indique pas si ta réponse était juste ou fausse.</p><div className="qcm-waiting-dots"><i /><i /><i /></div></section>}

    {session.status === "finished" && <section className="qcm-student-state qcm-student-finished"><Check size={48} /><h1>QCM terminé</h1><p>{session.mode === "evaluated" ? "Ta note sera enregistrée dans ta fiche CompetenceO." : "Ce QCM était un diagnostic de prérequis : aucune note n’est attribuée."}</p><a href="/">Retour à CompetenceO</a></section>}
  </main>;
}
